import type {
  ArticleCategory,
  UserInput
} from "@input-output-hk/essential-cardano-models";
import { normalizeSlug } from "@input-output-hk/essential-cardano-utils";
import { FastField, FastFieldProps, Form, Formik, FormikErrors } from "formik";
import { useRouter } from "next/router";
import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { ApiErrorResponse } from "../../../api-utils/errors";
import type {
  Contribution,
  ContributionSource,
  Facets
} from "../../../data/db/types";
import useFacets from "../../../hooks/useFacets";
import { usePortal } from "../../../hooks/usePortal";
import { MaybeApiError } from "../../../hooks/useSWR";
import colors from "../../../tokens/colors";
import { Maybe, Nullable } from "../../../utils/types";
import AlertDialogBox from "../../alert-dialog/AlertDialog";
import Button from "../../buttons/Button";
import Heading from "../../heading/Heading";
import { Column, Stack } from "../../Stack";
import Text from "../../text/Text";
import MarkdownField from "../MarkdownField";
import SlugField from "../SlugField";
import TagsField from "../TagsField";
import TextField from "../TextField";
import {
  ArticleTemplate,
  ContributeFormSchemaTemplate,
  isImageUploadField
} from "./form-schema";
import { shouldUpdateFastField } from "./ContributeForm.helpers";
import { isValidHttpUrl, parseUrl } from "../../../utils/url-utils";
import SelectImageFieldset from "../../select-image-fieldset/SelectImageFieldset";
import { pxToRem } from "@input-output-hk/px-to-rem";

const ContributeFormElement = styled.div`
  &[data-enabled="false"] {
    --input-border-color: ${colors.secondary.londonFog};
  }
`;

const HeroImageContainer = styled.div`
  background-color: ${colors.secondary.londonFog};
  padding: var(--spacing-large);
  padding-bottom: calc(var(--spacing-default) + var(--spacing-large));
`;

/**
 *  && = Increase CSS specificity without using `!important`
 */
const ButtonContainer = styled.div`
  && {
    margin-top: var(--spacing-level0);
  }
`;
const ErrorDialog = styled(Column)`
  padding: var(--spacing-large);
`;
const ErrorDialogMessage = styled.div`
  padding: var(--spacing-large);
  border: ${pxToRem(1)} solid ${colors.feedback.errorRedLight};
  color: ${colors.feedback.errorRed};
  border-radius: ${pxToRem(15)};
`;

type Props = {
  enabled?: boolean;
  facets?: Nullable<Facets>;
  contribution?: Nullable<Contribution | ContributionSource>;
  defaultCategory?: ArticleCategory["type"];
  showSelectCategory?: boolean;
  showAddImage?: boolean;
  showAddUrl?: boolean;
  schema?: ContributeFormSchemaTemplate;
  onSaveSuccess: (updatedContribution: Contribution) => void;
};

type FormValues = UserInput & {
  _sourceId?: string;
};

const defaultValues: FormValues = {
  _tags: [],
  _category: "article",
  url: "",
  title: "",
  body: "",
  image: "",
  image_text: "",
  slug: ""
};

export const FIELD_LABELS = {
  title: "Title",
  body: "Content",
  image: "Image",
  image_text: "Image Description",
  url: "URL",
  slug: "URL slug",
  _tags: "Tags",
  _category: "Category"
} as const;

const getInitialFormValues = (
  contribution: Contribution | ContributionSource | null,
  overrides: Partial<FormValues>
) => {
  // Return the source values...
  if (contribution && contribution._userInput) {
    return "_sourceId" in contribution
      ? {
          ...contribution._userInput,
          _sourceId: contribution._sourceId
        }
      : contribution._userInput;
  }

  let cachedValues: Partial<FormValues> = {};
  if (typeof window !== "undefined") {
    try {
      const raw = window.localStorage.getItem(LOCAL_STORAGE_KEY);
      if (raw) {
        cachedValues = JSON.parse(raw) as Partial<FormValues>;
      }
    } catch (err) {
      console.error(err);
    }
  }

  // ...Or return default values with overrides
  const values: FormValues = { ...defaultValues, ...cachedValues };

  for (const [key, value] of Object.entries(overrides)) {
    if (value !== undefined && value !== null) {
      values[key as keyof FormValues] = value as any;
    }
  }

  return values;
};

const LOCAL_STORAGE_KEY = "__contribute__";

const LocalStorageSideEffectComponent: React.FC<{
  storageKey?: string;
  values: Partial<FormValues>;
}> = ({ storageKey, values }) => {
  const valuesString = JSON.stringify(values);
  useEffect(() => {
    if (storageKey) {
      try {
        window.localStorage.setItem(storageKey, valuesString);
      } catch (err) {
        console.error(err);
      }
    }
  }, [valuesString, storageKey]);
  return null;
};

function isContribution(
  obj: Maybe<Contribution | ContributionSource>
): obj is Contribution {
  return !!obj && "_id" in obj;
}

type RequestErrorResponse = string;
type ValidationErrorResponse = Record<string, string>;

type SubmitResponse = MaybeApiError<
  { data: { submission: Contribution } },
  RequestErrorResponse | ValidationErrorResponse
>;
type SubmitRequestError = ApiErrorResponse<RequestErrorResponse>;

const ContributeForm: React.FC<Props> = ({
  enabled = true,
  facets: givenFacets = null,
  contribution: givenContribution = null,
  defaultCategory = "article",
  schema = ArticleTemplate,
  onSaveSuccess
}) => {
  const router = useRouter();
  const facets = useFacets(givenFacets);
  const id = router.query._id as string | undefined;
  const defaultTags = facets?._tags ? Object.keys(facets._tags) : [];
  const [requestError, setRequestError] = useState<SubmitRequestError | null>(
    null
  );
  const portal = usePortal();
  const [contribution, setContribution] = useState<
    Contribution | ContributionSource | null
  >(givenContribution);

  const isNew = !contribution;

  const initialValues = getInitialFormValues(contribution, {
    _category: defaultCategory
  });

  useEffect(() => {
    if (id) {
      // Not use SWR here because it re-requests on window refocus, we only
      // want to do this on load
      fetch(`/api/contribute/${id}`)
        .then(
          (r) =>
            r.json() as Promise<
              MaybeApiError<{ data: { submission: Contribution } }>
            >
        )
        .then((r) => {
          if ("data" in r) {
            setContribution(r.data.submission);
          }
        });
    }
  }, [id]);

  return (
    <ContributeFormElement
      key={JSON.stringify(initialValues)}
      data-enabled={enabled}
    >
      <Formik<FormValues>
        initialValues={initialValues}
        validate={(values) => {
          const errors: FormikErrors<FormValues> = {};

          if (!values._category) {
            errors._category = "Please select a category";
          }

          if (values.image && !isValidHttpUrl(parseUrl(values.image))) {
            errors.image = "Please provide a valid HTTP link to an image";
          }

          schema.fields.forEach((field) => {
            if (isImageUploadField(field)) {
              return;
            }

            const validationError = field.validation?.(values[field.name]);
            if (validationError) {
              errors[field.name] = validationError.error;
            }
          });

          return errors;
        }}
        onSubmit={async (
          values,
          { setSubmitting, setErrors: setValidationErrors }
        ) => {
          values.url =
            values.url === undefined || values.url === ""
              ? ""
              : parseUrl(values.url);

          values.image =
            values.image === undefined || values.image === ""
              ? ""
              : parseUrl(values.image);

          /**
           * Empty slug means "derive one from the title"
           */
          values.slug = values.slug ? normalizeSlug(values.slug) : "";

          if (!enabled) {
            return;
          }
          console.log("submitting contribution form", values);
          const resp = await fetch(
            `/api/contribute${
              isContribution(contribution) ? `/${contribution._id}` : ""
            }`,
            {
              method: "POST",
              credentials: "include",
              headers: {
                "Content-Type": "application/json"
              },
              body: JSON.stringify(values)
            }
          ).then((res) => res.json() as Promise<SubmitResponse>);

          console.log({ resp });

          if ("error" in resp) {
            console.error(resp.error);
            if (typeof resp.error === "string") {
              setRequestError(resp as SubmitRequestError);
            } else {
              setValidationErrors(resp.error);
            }
          }

          if ("data" in resp && resp.data.submission) {
            try {
              window.localStorage.removeItem(LOCAL_STORAGE_KEY);
            } catch (err) {
              console.error(err);
            }
            onSaveSuccess(resp.data.submission);
          }

          setSubmitting(false);
        }}
      >
        {({ isSubmitting, getFieldHelpers, values }) => (
          <Form>
            <LocalStorageSideEffectComponent
              values={values}
              storageKey={isNew ? LOCAL_STORAGE_KEY : undefined}
            />
            <Stack level="2">
              {schema.fields.map((field) => {
                if (isImageUploadField(field)) {
                  // TODO
                  return (
                    <HeroImageContainer key="image">
                      <SelectImageFieldset
                        name="image"
                        id="image"
                        enabled={enabled}
                        getFieldHelpers={getFieldHelpers}
                        onUploadError={(err) => setRequestError(err)}
                      />
                    </HeroImageContainer>
                  );
                }
                const { type, name, label, placeholder } = field;
                switch (type) {
                  case "slug":
                    /**
                     * Not a FastField - it needs the latest title
                     * to generate a slug from
                     */
                    return (
                      <SlugField
                        key={name}
                        label={label}
                        name={name}
                        id={name}
                        placeholder={placeholder}
                        disabled={!enabled}
                      />
                    );
                  case "text":
                    return (
                      <FastField
                        key={name}
                        name={name}
                        disabled={!enabled}
                        shouldUpdate={shouldUpdateFastField}
                      >
                        {(props: FastFieldProps) => (
                          <TextField
                            label={label}
                            name={name}
                            id={name}
                            placeholder={placeholder}
                            disabled={!enabled}
                            {...props}
                          />
                        )}
                      </FastField>
                    );
                  case "markdown":
                  case "markdown-large":
                    return (
                      <FastField
                        key={name}
                        name={name}
                        disabled={!enabled}
                        shouldUpdate={shouldUpdateFastField}
                      >
                        {(props: FastFieldProps) => (
                          <MarkdownField
                            label={label}
                            name={name}
                            placeholder={placeholder}
                            getFieldHelpers={getFieldHelpers}
                            disabled={!enabled}
                            appearance={
                              type === "markdown-large" ? "large" : "compact"
                            }
                            {...props}
                          />
                        )}
                      </FastField>
                    );
                  case "tags":
                    return (
                      <FastField
                        key={defaultTags.length}
                        name={name}
                        disabled={!enabled}
                        shouldUpdate={shouldUpdateFastField}
                      >
                        {(props: FastFieldProps) => (
                          <TagsField
                            label={label}
                            name={name}
                            id={name}
                            placeholder="Tag your content"
                            defaultTags={defaultTags}
                            getFieldHelpers={getFieldHelpers}
                            disabled={!enabled}
                            {...props}
                          />
                        )}
                      </FastField>
                    );
                }
              })}
              <ButtonContainer>
                <Button type="submit" disabled={isSubmitting || !enabled}>
                  {isSubmitting ? "Submitting" : "Submit for review"}
                </Button>
              </ButtonContainer>
            </Stack>
          </Form>
        )}
      </Formik>
      {requestError &&
        portal(
          <AlertDialogBox
            isOpen={true}
            onRequestClose={() => setRequestError(null)}
          >
            <ErrorDialog>
              <Heading level="2" isTight>
                Oh dear! Something went wrong
              </Heading>
              {requestError.code === 400 && (
                <Text>We are sorry we could not submit your contribution</Text>
              )}
              <ErrorDialogMessage>
                {requestError.code === 503
                  ? "It looks like we can't connect to github right now. This means we currently can't accept any content contributions or edits. You can check https://www.githubstatus.com/ to see when github is fully operational. In the meantime you can still browse, comment and vote on content."
                  : requestError.error}
              </ErrorDialogMessage>
              <Button onClick={() => setRequestError(null)}>Close</Button>
            </ErrorDialog>
          </AlertDialogBox>
        )}
    </ContributeFormElement>
  );
};

export default ContributeForm;
